import React, { useEffect, useState } from 'react';
import {
  Drawer, Box, Typography, Chip, Divider, CircularProgress,
  LinearProgress, Stack, IconButton, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ReplyIcon from '@mui/icons-material/Reply';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ForumIcon from '@mui/icons-material/Forum';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { intelligenceApi } from '../../api/intelligenceApi';

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const CHANNEL_CONFIG = {
  email:    { label: 'Email',    Icon: EmailIcon,    color: '#1565c0', bg: '#e3f2fd' },
  whatsapp: { label: 'WhatsApp', Icon: WhatsAppIcon, color: '#2e7d32', bg: '#e8f5e9' },
  voice:    { label: 'Voice',    Icon: ForumIcon,    color: '#6a1a6a', bg: '#f3e5f5' },
};

const CONV_STATUS_COLOR = {
  open:     '#e65100',
  resolved: '#2e7d32',
  closed:   '#757575',
};

const ORG_TYPE_CONFIG = {
  individual:   { label: 'Individual',    color: '#1565c0', bg: '#e3f2fd' },
  corporate:    { label: 'Corporate',     color: '#4a148c', bg: '#f3e5f5' },
  school:       { label: 'School',        color: '#1b5e20', bg: '#e8f5e9' },
  church:       { label: 'Church',        color: '#bf360c', bg: '#fbe9e7' },
  government:   { label: 'Government',    color: '#0d47a1', bg: '#e8eaf6' },
  ngo:          { label: 'NGO / Charity', color: '#e65100', bg: '#fff3e0' },
  travel_agent: { label: 'Travel Agent',  color: '#006064', bg: '#e0f7fa' },
  supplier:     { label: 'Supplier',      color: '#37474f', bg: '#eceff1' },
  unknown:      { label: 'Unknown',       color: '#757575', bg: '#f5f5f5' },
};

const STATUS_COLORS = {
  cold:      '#90caf9',
  warm:      '#ffb74d',
  hot:       '#ef5350',
  urgent:    '#b71c1c',
  inactive:  '#9e9e9e',
  converted: '#66bb6a',
};

const INTENT_CONFIG = {
  quote_request:        { label: 'Quote Request',    color: '#1565c0', bg: '#e3f2fd', border: '#1565c0', Icon: TravelExploreIcon },
  general_inquiry:      { label: 'General Inquiry',  color: '#2e7d32', bg: '#e8f5e9', border: '#2e7d32', Icon: EmailIcon },
  follow_up:            { label: 'Follow Up',        color: '#e65100', bg: '#fff3e0', border: '#e65100', Icon: ReplyIcon },
  complaint:            { label: 'Complaint',        color: '#b71c1c', bg: '#ffebee', border: '#b71c1c', Icon: WarningAmberIcon },
  booking_confirmation: { label: 'Booking Confirmed',color: '#1b5e20', bg: '#e8f5e9', border: '#1b5e20', Icon: CheckCircleOutlineIcon },
  unsubscribe:          { label: 'Unsubscribe',      color: '#6a1a6a', bg: '#f3e5f5', border: '#6a1a6a', Icon: UnsubscribeIcon },
  noise:                { label: 'Noise',            color: '#757575', bg: '#f5f5f5', border: '#bdbdbd', Icon: RemoveCircleOutlineIcon },
};

export default function ContactProfileDrawer({ contactId, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    intelligenceApi.getContact(contactId)
      .then(setData)
      .finally(() => setLoading(false));
  }, [contactId]);

  return (
    <Drawer anchor="right" open={Boolean(contactId)} onClose={onClose}
      PaperProps={{ sx: { width: 480, p: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
          Contact Profile
        </Typography>
        <IconButton onClick={() => onClose?.()} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <Box sx={{ px: 3, py: 2, overflowY: 'auto', height: '100%' }}>
          {/* Identity */}
          <Typography variant="subtitle1" fontWeight={700}>{data.name}</Typography>
          <Typography variant="body2" color="text.secondary">{data.email}</Typography>
          {data.company && (
            <Typography variant="body2" color="text.secondary">{data.company}</Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Status + Readiness */}
          <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
            <Chip
              label={data.profile.operational_status?.toUpperCase()}
              size="small"
              sx={{ bgcolor: STATUS_COLORS[data.profile.operational_status] || '#ccc', color: '#fff', fontWeight: 700 }}
            />
            <Chip label={data.profile.primary_interest?.replace(/_/g, ' ')} size="small" variant="outlined" />
            {(() => {
              const orgType = data.profile.org_type;
              if (!orgType || orgType === 'unknown') return null;
              const cfg = ORG_TYPE_CONFIG[orgType] || ORG_TYPE_CONFIG.unknown;
              return (
                <Chip
                  label={cfg.label}
                  size="small"
                  sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600 }}
                />
              );
            })()}
            {data.is_hot_client && <Chip label="HOT CLIENT" size="small" color="error" />}
          </Stack>

          <Typography variant="body2" color="text.secondary" mb={0.5}>
            Buying Readiness — {data.profile.buying_readiness_score}/100
          </Typography>
          <LinearProgress
            variant="determinate"
            value={data.profile.buying_readiness_score}
            sx={{ height: 8, borderRadius: 4, mb: 2 }}
            color={data.profile.buying_readiness_score >= 75 ? 'error' : data.profile.buying_readiness_score >= 45 ? 'warning' : 'primary'}
          />

          {/* AI Summary */}
          {data.profile.ai_summary && (
            <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 2, p: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                AI SUMMARY
              </Typography>
              <Typography variant="body2">{data.profile.ai_summary}</Typography>
            </Box>
          )}

          {/* Recommended Action */}
          {data.profile.recommended_next_action && (
            <Box sx={{ bgcolor: '#e8f5e9', borderRadius: 2, p: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                RECOMMENDED NEXT ACTION
              </Typography>
              <Typography variant="body2">{data.profile.recommended_next_action}</Typography>
            </Box>
          )}

          {/* Tags */}
          {data.tags?.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                INTELLIGENCE TAGS
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {data.tags.map((t) => (
                  <Tooltip key={t.tag} title={`Confidence: ${t.confidence}% · Source: ${t.source}`}>
                    <Chip label={t.tag} size="small" sx={{ fontSize: 11 }} />
                  </Tooltip>
                ))}
              </Box>
            </>
          )}

          {/* Stats */}
          <Divider sx={{ my: 2 }} />

          {/* Last active + preferred channel */}
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
            <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">
              Last active: <strong>{timeAgo(data.profile.last_active_at)}</strong>
            </Typography>
            {data.profile.preferred_channel && (() => {
              const ch = CHANNEL_CONFIG[data.profile.preferred_channel] || { label: data.profile.preferred_channel, Icon: ForumIcon, color: '#555', bg: '#f5f5f5' };
              return (
                <Chip
                  icon={<ch.Icon sx={{ fontSize: 12, color: `${ch.color} !important` }} />}
                  label={ch.label}
                  size="small"
                  sx={{ fontSize: 10, height: 20, bgcolor: ch.bg, color: ch.color, ml: 'auto !important' }}
                />
              );
            })()}
          </Stack>

          <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
            <Box sx={{ textAlign: 'center', minWidth: 60 }}>
              <Typography variant="h6" fontWeight={700} lineHeight={1}>{data.profile.total_inbound_emails ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">Inbound Emails</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 60 }}>
              <Typography variant="h6" fontWeight={700} lineHeight={1}>{data.profile.total_conversations ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">Conversations</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 60 }}>
              <Typography variant="h6" fontWeight={700} lineHeight={1}>{data.profile.total_campaigns_received ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">Campaigns Sent</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 60 }}>
              <Typography variant="h6" fontWeight={700} lineHeight={1}>{data.profile.total_campaigns_opened ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">Campaigns Opened</Typography>
            </Box>
          </Stack>

          {/* Recent unibox conversations */}
          {data.conversations?.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                RECENT CONVERSATIONS
              </Typography>
              <Stack spacing={1} mb={2}>
                {data.conversations.map((conv) => {
                  const ch = CHANNEL_CONFIG[conv.channel] || { label: conv.channel, Icon: ForumIcon, color: '#555', bg: '#f5f5f5' };
                  const statusColor = CONV_STATUS_COLOR[conv.status] || '#555';
                  return (
                    <Box
                      key={conv.id}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderLeft: `3px solid ${ch.color}`,
                        borderRadius: 1.5,
                        p: 1.25,
                        bgcolor: '#fafafa',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                        <ch.Icon sx={{ fontSize: 13, color: ch.color }} />
                        <Typography variant="caption" fontWeight={600} sx={{ color: ch.color, flex: 1 }} noWrap>
                          {conv.subject || '(No subject)'}
                        </Typography>
                        <Chip
                          label={conv.status}
                          size="small"
                          sx={{ fontSize: 9, height: 16, bgcolor: statusColor + '18', color: statusColor, fontWeight: 600 }}
                        />
                      </Stack>
                      {conv.preview && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ mb: 0.25 }}>
                          {conv.preview}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.disabled">
                        {timeAgo(conv.last_activity_at)}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}

          {/* Classification History */}
          {data.classification_history?.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                EMAIL CLASSIFICATION HISTORY
              </Typography>
              <Stack spacing={1.5}>
                {data.classification_history.map((c) => {
                  const cfg = INTENT_CONFIG[c.intent] || {
                    label: c.intent, color: '#555', bg: '#f5f5f5', border: '#bdbdbd', Icon: EmailIcon,
                  };
                  const readiness = c.buying_readiness ?? 0;
                  const readinessColor = readiness >= 70 ? '#2e7d32' : readiness >= 40 ? '#e65100' : '#9e9e9e';
                  const date = new Date(c.classified_at);
                  const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                  const timeStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

                  return (
                    <Box
                      key={c.id}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderLeft: `4px solid ${cfg.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: '#fafafa',
                        transition: 'box-shadow 0.2s',
                        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
                      }}
                    >
                      {/* Header row */}
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Chip
                          icon={<cfg.Icon sx={{ fontSize: 14, color: `${cfg.color} !important` }} />}
                          label={cfg.label}
                          size="small"
                          sx={{
                            bgcolor: cfg.bg,
                            color: cfg.color,
                            fontWeight: 600,
                            fontSize: 11,
                            '& .MuiChip-icon': { ml: 0.5 },
                          }}
                        />
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                            {dateStr}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" display="block" lineHeight={1.2}>
                            {timeStr}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Details row */}
                      <Stack direction="row" flexWrap="wrap" gap={0.5} mb={1}>
                        {c.service_type && c.service_type !== 'unknown' && (
                          <Chip
                            label={c.service_type.replace(/_/g, ' ')}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 10, height: 20, borderColor: '#bdbdbd', color: '#555' }}
                          />
                        )}
                        {c.destination && (
                          <Chip
                            label={c.destination}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 10, height: 20, borderColor: '#bdbdbd', color: '#555' }}
                          />
                        )}
                      </Stack>

                      {/* Readiness bar */}
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LinearProgress
                          variant="determinate"
                          value={readiness}
                          sx={{
                            flex: 1,
                            height: 5,
                            borderRadius: 3,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': { bgcolor: readinessColor, borderRadius: 3 },
                          }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: readinessColor, minWidth: 36, textAlign: 'right' }}
                        >
                          {readiness}/100
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </Box>
      )}
    </Drawer>
  );
}
